/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import React from 'react';
import { Link, Table, VisuallyHidden } from '@radix-ui/themes';

import { SortableTableHeader, type SortDirection } from '../../../../../shared/SortableTableHeader';
import { SeverityBadge } from '../SeverityBadge';
import { CONTAINER_STRINGS as STRINGS } from './containerQuarantine.strings';
import type { ContainerSortField, QuarantinedContainer } from './containerQuarantine.types';
import { isPlainPrimaryClick } from './plainClick';

const TIME_FORMAT = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });

function formatTime(value: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : TIME_FORMAT.format(date);
}

export interface ContainerTableProps {
  items: QuarantinedContainer[];
  sortBy: ContainerSortField;
  asc: boolean;
  onSort: (sortBy: ContainerSortField, asc: boolean) => void;
  /** When set, a click anywhere on a row opens the image, and its name is a link to `detailHref`. */
  onOpen?: (item: QuarantinedContainer) => void;
  detailHref?: (item: QuarantinedContainer) => string;
}

export function ContainerTable({ items, sortBy, asc, onSort, onOpen, detailHref }: ContainerTableProps): JSX.Element {
  const handleSort = (key: string, direction: SortDirection) => onSort(key as ContainerSortField, direction === 'asc');
  const sortable = (key: ContainerSortField, label: string) => (
    <SortableTableHeader
      sortKey={key}
      currentSortKey={sortBy}
      currentSortDirection={asc ? 'asc' : 'desc'}
      onSort={handleSort}
    >
      {label}
    </SortableTableHeader>
  );

  return (
    <Table.Root variant="surface" size="2">
      <caption>
        <VisuallyHidden>{STRINGS.caption}</VisuallyHidden>
      </caption>
      <Table.Header>
        <Table.Row>
          {sortable('SEVERITY', STRINGS.columns.severity)}
          <Table.ColumnHeaderCell>{STRINGS.columns.image}</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>{STRINGS.columns.tag}</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>{STRINGS.columns.digest}</Table.ColumnHeaderCell>
          {sortable('POLICY', STRINGS.columns.policy)}
          {sortable('QUARANTINE_TIME', STRINGS.columns.quarantineTime)}
          {sortable('REPOSITORY', STRINGS.columns.repository)}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {items.map((item) => (
          <Table.Row
            key={item.key}
            onClick={onOpen ? (event) => isPlainPrimaryClick(event) && onOpen(item) : undefined}
            className={onOpen ? 'container-row--clickable' : undefined}
          >
            <Table.Cell>
              <SeverityBadge threatLevel={item.threatLevel} severity={item.severity} />
            </Table.Cell>
            <Table.Cell>
              {onOpen && detailHref ? (
                // The link is the keyboard and screen reader way in; the row click is a pointer shortcut.
                <Link
                  size="2"
                  href={detailHref(item)}
                  data-analytics-id="nxrm-quarantine-container-detail-open"
                  onClick={(event) => {
                    // Not up to the row either way. A modified click is left to the browser (new tab or window).
                    event.stopPropagation();
                    if (isPlainPrimaryClick(event)) {
                      event.preventDefault();
                      onOpen(item);
                    }
                  }}
                >
                  {item.imageName ?? STRINGS.row.unknownImage}
                </Link>
              ) : (
                (item.imageName ?? STRINGS.row.unknownImage)
              )}
            </Table.Cell>
            <Table.Cell>{item.tag ?? '-'}</Table.Cell>
            <Table.Cell className="container-digest">{item.digest ?? '-'}</Table.Cell>
            <Table.Cell>{item.policyName}</Table.Cell>
            <Table.Cell>{formatTime(item.quarantineTime)}</Table.Cell>
            <Table.Cell>{item.repositoryName}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
}
